import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import type {
  RiskCropProfile,
  RiskCropProfileHistory,
} from "@/lib/api/risk/profiles";
import { Decimal } from "decimal.js";
import { formatNumberToDisplay } from "@/lib/api/common";
import { Checkbox } from "../ui/checkbox";
import { toSentenceCase } from "@/lib/prelude";

interface RiskProfileDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: RiskCropProfile | null;
  histories: RiskCropProfileHistory[] | null;
}

interface ComparableProfileValues {
  commodity_name: string | null;
  crop_year: number | null;
  acreage: string | null;
  projected_yield: string | null;
  actual_yield: string | null;
  projected_total_production: string | null;
  actual_total_production: string | null;
  cost_per_acre: string | null;
  is_enabled: boolean | null;
}

function getComparableValues(
  p: RiskCropProfileHistory | RiskCropProfile | undefined | null
): ComparableProfileValues {
  if (!p) {
    return {
      commodity_name: null,
      crop_year: null,
      acreage: null,
      projected_yield: null,
      actual_yield: null,
      projected_total_production: null,
      actual_total_production: null,
      cost_per_acre: null,
      is_enabled: null,
    };
  }
  return {
    commodity_name: p.commodity_name,
    crop_year: p.crop_year,
    acreage: p.acreage instanceof Decimal ? p.acreage.toString() : p.acreage ?? null,
    projected_yield: p.projected_yield instanceof Decimal ? p.projected_yield.toString() : p.projected_yield ?? null,
    actual_yield: p.actual_yield instanceof Decimal ? p.actual_yield.toString() : p.actual_yield ?? null,
    projected_total_production: p.projected_total_production instanceof Decimal ? p.projected_total_production.toString() : p.projected_total_production ?? null,
    actual_total_production: p.actual_total_production instanceof Decimal ? p.actual_total_production.toString() : p.actual_total_production ?? null,
    cost_per_acre: p.cost_per_acre instanceof Decimal ? p.cost_per_acre.toString() : p.cost_per_acre ?? null,
    is_enabled: typeof p.is_enabled === "boolean" ? p.is_enabled : null,
  };
}

const renderFieldValue = (
  currentValue: string | number | boolean | null,
  previousValue: string | number | boolean | null
) => {
  const displayValue = currentValue === null || currentValue === undefined ? "--" : String(currentValue);
  let style: React.CSSProperties = {};

  if (currentValue !== previousValue) {
    if ((previousValue === null || previousValue === undefined) && (currentValue !== null && currentValue !== undefined)) {
      style = { color: "green", fontWeight: "bold" }; // Added
    } else if ((currentValue === null || currentValue === undefined) && (previousValue !== null && previousValue !== undefined)) {
      style = { color: "red" }; // Removed
    } else {
      style = { fontWeight: "bold", color: "blue" }; // Changed
    }
  }

  return <span style={style}>{displayValue}</span>;
};


export function RiskProfileDetailModal({
  isOpen,
  onClose,
  profile,
  histories,
}: RiskProfileDetailModalProps) {
  const sortedHistories = histories
    ? [...histories].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    : [];

  const currentProfileValues = profile ? getComparableValues(profile) : undefined;

  // Convert history data for DataTable
  const historyTableData = useMemo(() => {
    return sortedHistories.map((historyEntry, index) => {
      const currentHistoryItemComparable = getComparableValues(historyEntry);
      const previousHistoryItemComparable = getComparableValues(sortedHistories[index + 1]);

      return {
        id: historyEntry.id,
        changed_at: `${new Date(historyEntry.created_at).toLocaleString()} by ${historyEntry?.User?.name}`,
        action: toSentenceCase(historyEntry.history_type),
        commodity_name: currentHistoryItemComparable.commodity_name,
        crop_year: currentHistoryItemComparable.crop_year,
        acreage: currentHistoryItemComparable.acreage,
        projected_yield: currentHistoryItemComparable.projected_yield,
        actual_yield: currentHistoryItemComparable.actual_yield,
        projected_total_production: currentHistoryItemComparable.projected_total_production,
        actual_total_production: currentHistoryItemComparable.actual_total_production,
        cost_per_acre: currentHistoryItemComparable.cost_per_acre,
        is_enabled: currentHistoryItemComparable.is_enabled,
        // Store previous values for comparison
        previous: previousHistoryItemComparable,
      };
    });
  }, [sortedHistories]);

  // Define columns for DataTable
  const columns = useMemo((): DataTableColumn<any>[] => [
    {
      id: "changed_at",
      header: "Changed At",
      accessorKey: "changed_at",
      cell: ({ row }) => row.original.changed_at,
      size: 200,
    },
    {
      id: "action",
      header: "Action",
      accessorKey: "action",
      cell: ({ row }) => row.original.action,
      size: 100,
    },
    {
      id: "commodity_name",
      header: "Commodity",
      accessorKey: "commodity_name",
      cell: ({ row }) => renderFieldValue(
        row.original.commodity_name,
        row.original.previous.commodity_name
      ),
      size: 120,
    },
    {
      id: "crop_year",
      header: "Crop Year",
      accessorKey: "crop_year",
      cell: ({ row }) => renderFieldValue(
        row.original.crop_year,
        row.original.previous.crop_year
      ),
      size: 100,
    },
    {
      id: "acreage",
      header: "Acreage",
      accessorKey: "acreage",
      cell: ({ row }) => renderFieldValue(
        row.original.acreage,
        row.original.previous.acreage
      ),
      size: 100,
    },
    {
      id: "projected_yield",
      header: "Projected Yield",
      accessorKey: "projected_yield",
      cell: ({ row }) => renderFieldValue(
        row.original.projected_yield,
        row.original.previous.projected_yield
      ),
      size: 130,
    },
    {
      id: "actual_yield",
      header: "Actual Yield",
      accessorKey: "actual_yield",
      cell: ({ row }) => renderFieldValue(
        row.original.actual_yield,
        row.original.previous.actual_yield
      ),
      size: 120,
    },
    {
      id: "projected_total_production",
      header: "Projected Production",
      accessorKey: "projected_total_production",
      cell: ({ row }) => renderFieldValue(
        formatNumberToDisplay(row.original.projected_total_production, { passthroughEmpty: true }),
        formatNumberToDisplay(row.original.previous.projected_total_production, { passthroughEmpty: true })
      ),
      size: 150,
    },
    {
      id: "actual_total_production",
      header: "Actual Production",
      accessorKey: "actual_total_production",
      cell: ({ row }) => renderFieldValue(
        formatNumberToDisplay(row.original.actual_total_production, { passthroughEmpty: true }),
        formatNumberToDisplay(row.original.previous.actual_total_production, { passthroughEmpty: true })
      ),
      size: 140,
    },
    {
      id: "cost_per_acre",
      header: "Cost Per Acre",
      accessorKey: "cost_per_acre",
      cell: ({ row }) => renderFieldValue(
        formatNumberToDisplay(row.original.cost_per_acre, { passthroughEmpty: true }),
        formatNumberToDisplay(row.original.previous.cost_per_acre, { passthroughEmpty: true })
      ),
      size: 120,
    },
    {
      id: "is_enabled",
      header: "Visible",
      accessorKey: "is_enabled",
      cell: ({ row }) => renderFieldValue(
        row.original.is_enabled ? "Yes" : "No",
        row.original.previous.is_enabled ? "Yes" : "No"
      ),
      size: 100,
    },
  ], []);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] block">
        <DialogHeader>
          <DialogTitle>
            {profile?.Customer?.long_name || "Loading"}
          </DialogTitle>
          <DialogDescription className="sr-only">Profile Detail Modal</DialogDescription>
        </DialogHeader>

        {!profile && (<p></p>)}
        {(profile && !!currentProfileValues) && (
          <div className="space-y-4 py-4">
            <h3 className="text-lg font-semibold">Current Profile</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <p className="flex items-center gap-2"><strong>Commodity:</strong> {currentProfileValues.commodity_name}</p>
              <p className="flex items-center gap-2"><strong>Crop Year:</strong> {currentProfileValues.crop_year}</p>
              <p className="flex items-center gap-2"><strong>Acreage:</strong> {formatNumberToDisplay(currentProfileValues.acreage, { emptyFormat: "--" })}</p>
              <p className="flex items-center gap-2"><strong>Projected Yield:</strong> {formatNumberToDisplay(currentProfileValues.projected_yield, { emptyFormat: "--" })}</p>
              <p className="flex items-center gap-2"><strong>Actual Yield:</strong> {formatNumberToDisplay(currentProfileValues.actual_yield, { emptyFormat: "--" })}</p>
              <p className="flex items-center gap-2"><strong>Projected Production:</strong> {formatNumberToDisplay(currentProfileValues.projected_total_production, { emptyFormat: "--" })}</p>
              <p className="flex items-center gap-2"><strong>Actual Production:</strong> {formatNumberToDisplay(currentProfileValues.actual_total_production, { emptyFormat: "--" })}</p>
              <p className="flex items-center gap-2"><strong>Cost Per Acre:</strong> {formatNumberToDisplay(currentProfileValues.cost_per_acre, { emptyFormat: "--" })}</p>
              <p className="flex items-center gap-2"><strong>Visbile:</strong> {currentProfileValues.is_enabled !== null ? <Checkbox checked={currentProfileValues.is_enabled} disabled /> : "N/A"}</p>
            </div>

            <h3 className="text-lg font-semibold">Change History</h3>
            {historyTableData.length > 0 ? (
              <DataTable
                className="shadow-lg"
                data={historyTableData}
                columns={columns}
                stickyHeaders
                enableVirtualization
                virtualizationRowHeight={65}
                virtualizationOverscan={10}
                enableColumnResizing
                filterStyle='select'
                filterMode='internal'
                tableClassName="h-[50vh] overflow-y-auto"
                emptyMessage="No history available for this profile."
                enableFiltering={false}
                enableExport={false}
                enableColumnVisibility={false}
              />
            ) : (
              <p>No history available for this profile.</p>
            )}
          </div>

        )}


        <DialogFooter>
          <Button onClick={onClose} variant="outline">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
